import { useSearchParams } from 'react-router-dom';

import { getApiFilters } from '~/shared/utils/apiFilters';
import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../../common/serachParamKeys';
import {
  AUTOSCALING_STATE_PARAM_KEY,
  FilterStoredInURL,
  createAutoscalingFilters,
  filterNonSpecificGroups,
  combineFilters,
} from '../utils/filterUtils';

export function useFilterUrlParams() {
  const [_searchParams, setSearchParams] = useSearchParams();

  const applyFilters = (
    pendingAutoscaleState: string[],
    pendingClusterNameFilters: SelectOption[],
    pendingVolumeIdFilters: SelectOption[],
    pendingVolumeTagFilters: SelectOption[],
    pendingInstanceFilters: SelectOption[]
  ) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = JSON.parse(
          prevParams.get(apiFilterKey) || '[]'
        ) as FilterStoredInURL[];

        const baseApiFilters = filterNonSpecificGroups(existingFiltersFromUrl);
        const autoscalingFilters = createAutoscalingFilters(pendingAutoscaleState);
        const finalApiFilters = combineFilters(
          baseApiFilters,
          pendingClusterNameFilters,
          pendingVolumeIdFilters,
          pendingVolumeTagFilters,
          pendingInstanceFilters,
          autoscalingFilters
        );

        // Update autoscaling state parameter
        if (pendingAutoscaleState.length > 0) {
          updatedParams.set(AUTOSCALING_STATE_PARAM_KEY, pendingAutoscaleState.join(','));
        } else {
          updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
        }

        // Update filter parameters
        if (finalApiFilters.length > 0) {
          updatedParams.set(apiFilterKey, JSON.stringify(finalApiFilters));
          const processedFilters = getApiFilters(finalApiFilters);
          updatedParams.set('processed_filters', JSON.stringify(processedFilters));
        } else {
          updatedParams.delete(apiFilterKey);
          updatedParams.delete('processed_filters');
        }

        return updatedParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const updatedParams = new URLSearchParams(prev);
        updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
        updatedParams.delete('processed_filters');

        const existingFilters = JSON.parse(prev.get(apiFilterKey) || '[]') as FilterStoredInURL[];
        const nonVolumeInstanceAndNonAutoscalingFilters = filterNonSpecificGroups(existingFilters);

        if (nonVolumeInstanceAndNonAutoscalingFilters.length > 0) {
          updatedParams.set(
            apiFilterKey,
            JSON.stringify(nonVolumeInstanceAndNonAutoscalingFilters)
          );
        } else {
          updatedParams.delete(apiFilterKey);
        }

        return updatedParams;
      },
      { replace: true }
    );
  };

  return { applyFilters, resetFilters };
}
